import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useToast } from "@/hooks/use-toast";
import { Link, useParams, useLocation, useSearch } from "wouter";
import { format } from "date-fns";
import { Calendar, Clock, Car, MapPin, Loader2, Wallet, CreditCard, ShoppingCart, ShoppingBag, Star, Download, Repeat, Pencil, ArrowRight, ArrowLeft, XCircle } from "lucide-react";
import { useOrder, useCancelOrder } from "@/hooks/use-orders";
import { useServices } from "@/hooks/use-services";
import { usePublicSettings } from "@/hooks/use-settings";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ReviewDialog } from "@/components/ReviewDialog";
import type { OrderStatus, PaymentStatus } from "@/lib/types";
import { downloadInvoice } from "@/lib/invoice";
import { formatOrderAddress } from "@/lib/utils";
import { buildCartFromOrder, setReorderItems } from "@/lib/reorder";

function getStatusColor(status: OrderStatus): string {
  switch (status) {
    case 'pending':
      return 'bg-yellow-500/10 text-yellow-600 border-yellow-500/20';
    case 'confirmed':
      return 'bg-blue-500/10 text-blue-600 border-blue-500/20';
    case 'in_progress':
      return 'bg-purple-500/10 text-purple-600 border-purple-500/20';
    case 'completed':
      return 'bg-green-500/10 text-green-600 border-green-500/20';
    case 'cancelled':
      return 'bg-red-500/10 text-red-600 border-red-500/20';
    default:
      return 'bg-muted text-muted-foreground';
  }
}

function getPaymentStatusColor(status: PaymentStatus | undefined): string {
  switch (status) {
    case 'pending':
      return 'bg-yellow-500/10 text-yellow-600 border-yellow-500/20';
    case 'paid':
      return 'bg-green-500/10 text-green-600 border-green-500/20';
    case 'failed':
      return 'bg-red-500/10 text-red-600 border-red-500/20';
    case 'expired':
      return 'bg-gray-500/10 text-gray-600 border-gray-500/20';
    default:
      return 'bg-muted text-muted-foreground';
  }
}

export default function OrderDetails() {
  const { t, i18n } = useTranslation();
  const { toast } = useToast();
  const isRtl = i18n.language === 'ar';
  const { id } = useParams<{ id: string }>();
  const [, navigate] = useLocation();
  const [downloading, setDownloading] = useState(false);
  const [reviewDialogOrderId, setReviewDialogOrderId] = useState<string | null>(null);

  const { data: order, isLoading } = useOrder(id || '');
  const { data: services } = useServices();
  const { data: publicSettings } = usePublicSettings();
  const canEditActive = publicSettings?.customerCanEditActiveOrder ?? false;
  const canCancelOrder = publicSettings?.customerCanCancelOrder ?? false;
  const cancelCutoffMinutes = publicSettings?.customerCancelCutoffMinutes ?? 60;
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const cancelMutation = useCancelOrder();

  // Arriving from a notification tap (push or in-app bell) on a completed order
  // that hasn't been rated yet: open the rating dialog straight away. The live
  // order is the source of truth here — the notification payload's status may
  // be stale by the time it's tapped.
  const fromNotification =
    new URLSearchParams(useSearch()).get("from") === "notification";
  const ratingPromptedRef = useRef(false);

  useEffect(() => {
    if (!fromNotification || ratingPromptedRef.current || !order) return;
    ratingPromptedRef.current = true;

    if (order.status === 'completed' && !order.reviewId) {
      setReviewDialogOrderId(order.id);
    }

    // Drop the marker so a refresh or back-navigation doesn't prompt again.
    navigate(`/orders/${order.id}`, { replace: true });
  }, [fromNotification, order, navigate]);

  if (isLoading) {
    return (
      <div className="min-h-screen pt-24 pb-24 bg-background flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!order) {
    return (
      <div className="min-h-screen pt-24 pb-24 bg-background" dir={isRtl ? "rtl" : "ltr"}>
        <div className="container mx-auto px-4 max-w-2xl text-center">
          <p className="text-muted-foreground">{t("orders.notFound")}</p>
          <Link href="/orders" className="text-primary underline mt-4 inline-block">
            {t("orders.title")}
          </Link>
        </div>
      </div>
    );
  }

  const hasMultipleItems = order.items && order.items.length > 0;
  // Products delivered with the wash; order.subtotal already includes them.
  const orderProducts = order.products || [];
  const productsSubtotal = order.productsSubtotal || 0;
  const serviceName = isRtl ? (order.serviceNameAr || order.serviceName) : (order.serviceNameEn || order.serviceName);
  const itemCount = hasMultipleItems ? order.items!.length : 1;
  const canCompletePayment = order.status === 'pending' && order.paymentStatus === 'pending' && order.paymentLink;

  const handleReorder = () => {
    const cartItems = buildCartFromOrder(order, services);
    if (cartItems.length === 0) return;
    setReorderItems(cartItems);
    navigate("/booking");
  };

  return (
    <div className="min-h-screen pt-24 pb-24 bg-background" dir={isRtl ? "rtl" : "ltr"}>
      <div className="container mx-auto px-4 max-w-2xl">
        {/* Back button */}
        <Link href="/orders" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-6">
          {isRtl ? <ArrowRight className="h-4 w-4" /> : <ArrowLeft className="h-4 w-4" />}
          {t("orders.title")}
        </Link>

        <Card className="overflow-hidden" data-testid={`card-order-details-${order.id}`}>
          <CardHeader className="pb-3">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div>
                {hasMultipleItems ? (
                  <CardTitle className="text-lg flex items-center gap-2">
                    <ShoppingCart className="h-5 w-5" />
                    {t("orders.itemsCount", { count: itemCount })}
                  </CardTitle>
                ) : (
                  <CardTitle className="text-lg">{serviceName}</CardTitle>
                )}
                <p className="text-sm text-muted-foreground mt-1">
                  {t("orders.orderNumber")}: {order.id.slice(-8).toUpperCase()}
                </p>
              </div>
              <div className="flex flex-col gap-1 items-end">
                <Badge className={getStatusColor(order.status)}>
                  {t(`orders.status.${order.status}`)}
                </Badge>
                {order.paymentStatus && (
                  <Badge className={getPaymentStatusColor(order.paymentStatus)} variant="outline">
                    {t(`orders.paymentStatus.${order.paymentStatus}`)}
                  </Badge>
                )}
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            {/* Multi-item order display */}
            {hasMultipleItems && (
              <div className="space-y-2 pb-3 border-b">
                {order.items!.map((item, index) => (
                  <div key={index} className="flex items-center justify-between text-sm bg-muted/50 rounded-lg p-2">
                    <div className="flex items-center gap-2">
                      <Car className="h-4 w-4 text-muted-foreground" />
                      <span className="font-medium">{item.serviceName}</span>
                      <Badge variant="outline" className="text-xs capitalize">
                        {t(`booking.${item.vehicleType}`)}
                      </Badge>
                      {item.quantity > 1 && (
                        <span className="text-muted-foreground">x{item.quantity}</span>
                      )}
                    </div>
                    <span className="font-medium">{item.subtotal.toFixed(2)} {t("services.currency")}</span>
                  </div>
                ))}
              </div>
            )}

            {/* Products the team brings to the appointment */}
            {orderProducts.length > 0 && (
              <div className="space-y-2 pb-3 border-b" data-testid="order-products">
                <p className="text-xs font-medium text-muted-foreground">{t("booking.products.title")}</p>
                {orderProducts.map((line) => (
                  <div key={line.productId} className="flex items-center justify-between text-sm bg-muted/50 rounded-lg p-2">
                    <div className="flex items-center gap-2 min-w-0">
                      <ShoppingBag className="h-4 w-4 text-muted-foreground shrink-0" />
                      <span className="font-medium truncate">{isRtl ? line.nameAr : line.nameEn}</span>
                      <span className="text-muted-foreground shrink-0">x{line.quantity}</span>
                    </div>
                    <span className="font-medium shrink-0">{line.subtotal.toFixed(2)} {t("services.currency")}</span>
                  </div>
                ))}
              </div>
            )}

            <div className="grid grid-cols-2 gap-3 text-sm">
              <div className="flex items-center gap-2 text-muted-foreground">
                <Calendar className="h-4 w-4" />
                <span>{format(new Date(order.appointmentDate), 'MMM dd, yyyy')}</span>
              </div>
              <div className="flex items-center gap-2 text-muted-foreground">
                <Clock className="h-4 w-4" />
                <span>{order.appointmentTime}</span>
              </div>
              {!hasMultipleItems && (
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Car className="h-4 w-4" />
                  <span className="capitalize">{t(`booking.${order.vehicleType}`)}</span>
                </div>
              )}
              <div className="flex items-center gap-2 font-semibold">
                <span>{(order.totalPrice || order.price || 0).toFixed(2)} {t("services.currency")}</span>
              </div>
            </div>

            {(order.districtNameEn || order.governorateNameEn) && (
              <div className="flex items-start gap-2 text-sm text-muted-foreground pt-2 border-t">
                <MapPin className="h-4 w-4 mt-0.5 shrink-0" />
                <span>
                  {isRtl
                    ? `${order.districtNameAr || ''}${order.districtNameAr && order.governorateNameAr ? '، ' : ''}${order.governorateNameAr || ''}`
                    : `${order.districtNameEn || ''}${order.districtNameEn && order.governorateNameEn ? ', ' : ''}${order.governorateNameEn || ''}`
                  }
                </span>
              </div>
            )}

            {order.customerAddress && (
              <div className="flex items-start gap-2 text-sm text-muted-foreground">
                <span>{formatOrderAddress(order, t)}</span>
              </div>
            )}

            {/* Payment breakdown */}
            {(order.deliveryFee !== undefined && order.deliveryFee > 0) ||
             (order.discountAmount !== undefined && order.discountAmount > 0) ||
             (order.walletCreditUsed !== undefined && order.walletCreditUsed > 0) ||
             productsSubtotal > 0 ? (
              <div className="text-xs text-muted-foreground space-y-1 pt-2 border-t">
                {hasMultipleItems && order.subtotal !== undefined && (
                  <div className="flex justify-between">
                    <span>{t("orders.servicesSubtotal")}:</span>
                    <span>{(order.subtotal - productsSubtotal).toFixed(2)} {t("services.currency")}</span>
                  </div>
                )}
                {productsSubtotal > 0 && (
                  <div className="flex justify-between">
                    <span>{t("booking.products.subtotal")}:</span>
                    <span>{productsSubtotal.toFixed(2)} {t("services.currency")}</span>
                  </div>
                )}
                {order.deliveryFee !== undefined && order.deliveryFee > 0 && (
                  <div className="flex justify-between">
                    <span>{t("orders.deliveryFee")}:</span>
                    <span>{order.deliveryFee.toFixed(2)} {t("services.currency")}</span>
                  </div>
                )}
                {order.discountAmount !== undefined && order.discountAmount > 0 && (
                  <div className="flex justify-between text-green-600">
                    <span>{t("orders.discount")}:</span>
                    <span>-{order.discountAmount.toFixed(2)} {t("services.currency")}</span>
                  </div>
                )}
                {order.walletCreditUsed !== undefined && order.walletCreditUsed > 0 && (
                  <div className="flex justify-between text-primary">
                    <span className="flex items-center gap-1">
                      <Wallet className="h-3 w-3" />
                      {t("orders.walletCredit")}:
                    </span>
                    <span>-{order.walletCreditUsed.toFixed(2)} {t("services.currency")}</span>
                  </div>
                )}
                {order.cashAmountDue !== undefined && order.cashAmountDue > 0 && (
                  <div className="flex justify-between font-medium text-foreground pt-1 border-t">
                    <span className="flex items-center gap-1">
                      <CreditCard className="h-3 w-3" />
                      {t("orders.cashDue")}:
                    </span>
                    <span>{order.cashAmountDue.toFixed(2)} {t("services.currency")}</span>
                  </div>
                )}
              </div>
            ) : null}
            {order.notes && (
              <div className="text-sm text-muted-foreground pt-2 border-t">
                <span className="font-medium">{t("orders.notes")}:</span> {order.notes}
              </div>
            )}
            {order.cancellationReason && (
              <div className="text-sm text-destructive pt-2 border-t">
                <span className="font-medium">{t("orders.cancellationReason")}:</span> {order.cancellationReason}
              </div>
            )}

            {/* Actions */}
            <div className="pt-3 border-t flex flex-wrap gap-2">
              {canCompletePayment && (
                <Button
                  size="sm"
                  onClick={() => window.location.href = order.paymentLink!}
                  data-testid="button-complete-payment"
                >
                  <CreditCard className="h-4 w-4 me-2" />
                  {t("orders.completePayment")}
                </Button>
              )}
              {canEditActive && (order.status === 'pending' || order.status === 'confirmed') && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => navigate(`/orders/${order.id}/edit`)}
                  data-testid="button-edit-order"
                >
                  <Pencil className="h-4 w-4 me-2" />
                  {t("orders.editOrder")}
                </Button>
              )}
              {canCancelOrder &&
                (order.status === 'pending' || order.status === 'confirmed') &&
                // Mirrors the server's cutoff so the button hides once it's
                // too late; the server enforces the same rule on the call.
                !(order.appointmentDateTime &&
                  new Date(order.appointmentDateTime).getTime() < Date.now() + cancelCutoffMinutes * 60 * 1000) && (
                <Button
                  size="sm"
                  variant="outline"
                  className="text-destructive border-destructive/40 hover:bg-destructive/10"
                  onClick={() => setCancelDialogOpen(true)}
                  data-testid="button-cancel-order"
                >
                  <XCircle className="h-4 w-4 me-2" />
                  {t("orders.cancelOrder")}
                </Button>
              )}
              {order.status === 'completed' && !order.reviewId && (
                <Button
                  size="sm"
                  variant="default"
                  onClick={() => setReviewDialogOrderId(order.id)}
                  data-testid="button-rate-order"
                >
                  <Star className="h-4 w-4 me-2" />
                  {t("reviews.submitReview")}
                </Button>
              )}
              {order.status === 'completed' && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleReorder}
                  data-testid="button-reorder"
                >
                  <Repeat className="h-4 w-4 me-2" />
                  {t("orders.reorder")}
                </Button>
              )}
              {order.paymentStatus === 'paid' && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={async () => {
                    setDownloading(true);
                    const ok = await downloadInvoice(order.id);
                    setDownloading(false);
                    // Failures used to vanish into console.error — surface them.
                    if (!ok) {
                      toast({
                        title: t("paymentSuccess.downloadFailed"),
                        variant: "destructive",
                      });
                    }
                  }}
                  disabled={downloading}
                  data-testid="button-download-invoice"
                >
                  {downloading ? <Loader2 className="h-4 w-4 me-2 animate-spin" /> : <Download className="h-4 w-4 me-2" />}
                  {downloading ? t("paymentSuccess.downloading") : t("paymentSuccess.downloadInvoice")}
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {reviewDialogOrderId && (
          <ReviewDialog
            orderId={reviewDialogOrderId}
            onSuccess={() => setReviewDialogOrderId(null)}
          />
        )}

        <Dialog open={cancelDialogOpen} onOpenChange={setCancelDialogOpen}>
          <DialogContent dir={isRtl ? "rtl" : "ltr"}>
            <DialogHeader>
              <DialogTitle>{t("orders.cancelConfirmTitle")}</DialogTitle>
              <DialogDescription>{t("orders.cancelConfirmDesc")}</DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setCancelDialogOpen(false)}
                disabled={cancelMutation.isPending}
                data-testid="button-cancel-keep"
              >
                {t("orders.cancelKeepOrder")}
              </Button>
              <Button
                variant="destructive"
                disabled={cancelMutation.isPending}
                onClick={async () => {
                  try {
                    await cancelMutation.mutateAsync({ id: order.id });
                    setCancelDialogOpen(false);
                    toast({ title: t("orders.cancelSuccess") });
                  } catch (error: any) {
                    setCancelDialogOpen(false);
                    toast({
                      title: t("orders.cancelFailed"),
                      description: error.message,
                      variant: "destructive",
                    });
                  }
                }}
                data-testid="button-cancel-confirm"
              >
                {cancelMutation.isPending && <Loader2 className="h-4 w-4 me-2 animate-spin" />}
                {t("orders.cancelConfirmAction")}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
